import { styled } from '@mui/material';
import React from 'react';
import { useAppConfigRedirect } from '@/context/AppConfig';
import { useAppConstantsContext } from '@/context/AppConstants';
import { SectionBaseProps } from '@/reports/contact/Interfaces';
import {
  ReportSection,
  RestoreAllDataButton,
  peopleTypes,
} from '@/reports/index';
import { DateRange } from '@/reports/shared/DateRange/DateRange';
import { FlexContainer } from '@/reports/shared/OverviewItems/components';
import { Box } from '@/theme/index';
import { fetchJobTitle, formatCompany } from '@/utils/index';

const DataContainer = styled(FlexContainer)(({ theme }) => ({
  alignItems: 'flex-start',
  overflow: 'auto',
  padding: `0 ${theme.spacing(1.25)} ${theme.spacing(1.25)}`,
  marginTop: '0',
  borderBottom: `1px solid ${theme.palette.secondary.light}`,
  flexDirection: 'column',
}));

const ImageContainer = styled('img')(() => ({
  width: '52px',
  height: '52px',
}));

const CompanyContainer = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: `${theme.spacing(6.5)} auto`,
  columnGap: theme.spacing(1.5),
  padding: `${theme.spacing(1.2)} 0`,
  marginBottom: theme.spacing(1.25),

  [theme.breakpoints.up('sm')]: {
    marginBottom: 0,
  },
}));

const CompanyNameContainer = styled('div')(({ theme }) => ({
  color: theme.palette.primary.main,
  fontSize: theme.typography.fontSize * 1.1,
  fontWeight: theme.typography.fontWeightBold,
  marginBottom: theme.spacing(1),
  cursor: 'pointer',
}));

const JobContainer = styled('div')(({ theme }) => ({
  '&:not(:last-child)': {
    paddingBottom: theme.spacing(1.2),
  },

  position: 'relative',
}));

const JobsContainer = styled('div')(({ theme }) => ({
  marginLeft: theme.spacing(1),
  paddingLeft: theme.spacing(1),

  '&::before': {
    content: '""',
    position: 'absolute',
    width: '2.5px',
    backgroundColor: theme.palette.secondary.light,
    top: theme.spacing(2.25),
    bottom: theme.spacing(0.75),
    left: '0',
  },

  '&::after': {
    content: '""',
    position: 'absolute',
    width: '12px',
    height: '12px',
    backgroundColor: theme.palette.primary.main,
    top: theme.spacing(0.3),
    borderRadius: '50%',
    zIndex: '1',
    left: theme.spacing(-0.6),
  },
}));

const JobTitleContainer = styled('div')(({ theme }) => ({
  fontSize: theme.typography.fontSize,
  fontWeight: theme.typography.fontWeightBold,
  marginBottom: theme.spacing(0.5),
  color: theme.palette.secondary.contrastText,
}));

interface CompanyProps {
  name: string;
  website: string;
  jobs: peopleTypes.Jobs;
}

function onImageError(event: React.SyntheticEvent, logo: string) {
  const imgElem = event.target as HTMLImageElement;

  // Handle error only once and then return to prevent infinite loop
  if (imgElem.classList.contains('src-error')) {
    return;
  }

  imgElem.src = logo;
  imgElem.classList.add('src-error');
}

interface CompanyLogoProps {
  website: string | null | undefined;
  defaultCompanyLogo: string;
}

function CompanyLogo({ website, defaultCompanyLogo }: CompanyLogoProps) {
  if (website) {
    return (
      <ImageContainer
        alt="Company Logo"
        src={`//logo.clearbit.com/${website}`}
        onError={(event) => onImageError(event, defaultCompanyLogo)}
      />
    );
  }

  return <ImageContainer alt="Company Logo" src={defaultCompanyLogo} />;
}

interface JobDataProps {
  multiple: boolean;
  children: React.ReactNode;
}

function JobDataContainer({ multiple, children }: JobDataProps) {
  if (multiple) {
    return <JobsContainer>{children}</JobsContainer>;
  }

  return <Box>{children}</Box>;
}

interface CompanyHash<T> {
  [key: string]: T[];
}

/**
 * Formats a Jobs array and groups them by Company Name
 * Also Orders the Job per Company by Start Date
 * @param jobs A Jobs object array
 * @returns A Hash of Objects in which the Company Name is the key and the values are an array of Jobs
 * and the Company Name/Website
 */
function jobsGroupedByCompany(jobs: peopleTypes.Jobs) {
  const jobsByCompany: CompanyHash<peopleTypes.Job> = {};
  const companies: CompanyProps[] = [];

  jobs.forEach((job) => {
    const company = job.company?.toLowerCase() || '';

    if (jobsByCompany[company]) {
      jobsByCompany[company].push(job);
    } else {
      jobsByCompany[company] = [job];
    }
  });

  Object.keys(jobsByCompany).forEach((company) => {
    let groupedJobs = jobsByCompany[company];

    groupedJobs = [
      ...groupedJobs.filter(
        (job) => job.period?.start_date || job.period?.end_date
      ),
      ...groupedJobs.filter(
        (job) => !job.period?.start_date && !job.period?.end_date
      ),
    ];

    const companyNames = groupedJobs.map((job) =>
      formatCompany(job.company || '')
    );
    const companyName =
      companyNames.find((name) => /[A-Z]/.test(name)) || companyNames[0];
    const websites = groupedJobs.map(
      (job) => job.company_profile?.website || ''
    );

    companies.push({
      name: companyName,
      website: websites[0],
      jobs: groupedJobs,
    });
  });

  return companies;
}

interface ClassifiedJobsProps {
  jobs: peopleTypes.Jobs;
  defaultCompanyLogo: string;
}

function ClassifiedJobs({
  jobs,
  defaultCompanyLogo,
}: ClassifiedJobsProps): JSX.Element {
  const {
    links: { baseUrl },
  } = useAppConstantsContext();
  const {
    routingUtils: { searchContactUrl },
  } = useAppConfigRedirect();

  const openSearch = (companyName: string) => {
    window.open(
      `${baseUrl}${searchContactUrl({ company: companyName })}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const companies = jobsGroupedByCompany(jobs);

  return (
    <>
      {companies.map((company) => (
        <DataContainer key={company.name}>
          <CompanyContainer>
            <CompanyLogo
              website={company.website}
              defaultCompanyLogo={defaultCompanyLogo}
            />
            <div>
              <CompanyNameContainer onClick={() => openSearch(company.name)}>
                {company.name}
              </CompanyNameContainer>

              {company.jobs?.map((job) => (
                <JobContainer key={`${company.website}-${job.title}`}>
                  <JobDataContainer multiple={company.jobs?.length > 1}>
                    <JobTitleContainer>{fetchJobTitle(job)}</JobTitleContainer>
                    <DateRange
                      datesLabel=""
                      firstDateSeen={job.period?.start_date?.full || ''}
                      lastDateSeen={job.period?.end_date?.full || ''}
                    />
                  </JobDataContainer>
                </JobContainer>
              ))}
            </div>
          </CompanyContainer>
        </DataContainer>
      ))}
    </>
  );
}

export interface ContactEmploymentSectionProps extends SectionBaseProps {
  /** The array of Job objects */
  jobs: peopleTypes.Jobs;
  /** The image of the Placeholder Icon */
  defaultCompanyLogo: string;
}

/**
 * This component displays the Employment Section of the Contact Report
 */
export function ContactEmploymentSection({
  personName,
  jobs,
  permalink,
  defaultCompanyLogo,
}: ContactEmploymentSectionProps) {
  return (
    <ReportSection
      title="Possible Employment History"
      sectionDescription=""
      sectionId="jobs-section"
      hasContent={jobs.length > 0}
      personName={personName}
    >
      <ClassifiedJobs jobs={jobs} defaultCompanyLogo={defaultCompanyLogo} />

      <RestoreAllDataButton items={jobs} permalink={permalink} />
    </ReportSection>
  );
}
